import axios from 'axios';

// Config
const ENDPOINT = '/actions/mg-motors-europe-module/site-controller/get-csrf';
const HEADER = 'X-CSRF-TOKEN';
const METHODS = ['post', 'put', 'delete', 'patch'];
const RETRY_FLAG = '__csrfRetry';

// State
let token = null;
let pending = null;

// Storage (check once)
const canStore = (() => {
    try {
        sessionStorage.setItem('_', '1');
        sessionStorage.removeItem('_');
        return true;
    } catch {
        return false;
    }
})();

// Cache operations
const getStored = () => (canStore ? sessionStorage.getItem('csrf') : null);

const store = (csrfToken) => {
    if (!canStore) {
        token = csrfToken;
        return;
    }
    try {
        sessionStorage.setItem('csrf', csrfToken);
    } catch {
        token = csrfToken;
    }
};

const clear = () => {
    token = null;
    if (canStore) {
        try {
            sessionStorage.removeItem('csrf');
        } catch {
            /* noop */
        }
    }
};

// Fetch with deduplication
const fetchToken = () => {
    if (pending) {
        return pending;
    }

    pending = axios
        .get(ENDPOINT)
        .then((res) => {
            pending = null;
            const csrfToken = res.data?.value;
            if (!csrfToken) {
                throw new Error('Invalid CSRF response');
            }
            store(csrfToken);
            return csrfToken;
        })
        .catch((err) => {
            pending = null;
            throw err;
        });

    return pending;
};

// Get token (cached or fetch)
const getToken = async () => {
    const cached = token || getStored();
    if (cached) {
        token = cached;
        return cached;
    }
    return fetchToken();
};

// Force-refresh: clear cache and fetch a new token
const refreshToken = () => {
    clear();
    return fetchToken();
};

// Error detection
const isCsrfError = (err) => {
    const { status } = err.response || {};
    // Retry on any 400/419 for mutating requests — Craft's CSRF rejection message
    // varies by locale so text matching is unreliable across 40+ sites.
    // Blanket retry is safe: the retry fetches a fresh token and re-posts; if the
    // error was not CSRF-related, the same 400 will return and be rejected as final.
    return status === 400 || status === 419;
};

// Helpers
const needsCsrf = (method) => METHODS.includes(method?.toLowerCase());
const hasCsrfHeader = (config) => !!config.headers[HEADER];

// Request interceptor - attach token to mutating requests
const attachCsrf = async (config) => {
    if (!needsCsrf(config.method)) {
        return config;
    }
    if (hasCsrfHeader(config)) {
        return config;
    }

    try {
        config.headers[HEADER] = await getToken();
    } catch {
        // Token fetch failed; let the request go without it.
        // The retry interceptor will handle the 400/419 response.
    }
    return config;
};

// Response interceptor - one-time retry on CSRF rejection
const handleCsrfError = async (err) => {
    const originalRequest = err.config;

    // Only retry mutating requests, only once
    if (!isCsrfError(err) || !needsCsrf(originalRequest?.method)) {
        return Promise.reject(err);
    }
    if (originalRequest[RETRY_FLAG]) {
        return Promise.reject(err);
    }

    // Mark to prevent infinite retry loops
    originalRequest[RETRY_FLAG] = true;

    try {
        const freshToken = await refreshToken();
        originalRequest.headers[HEADER] = freshToken;
        return axios(originalRequest);
    } catch {
        return Promise.reject(err);
    }
};

// Register interceptors (auto-init on import)
axios.interceptors.request.use(attachCsrf);
axios.interceptors.response.use((res) => res, handleCsrfError);
